import React, { forwardRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { format, subMonths, subWeeks, subQuarters, startOfQuarter, endOfQuarter } from 'date-fns';
import { FRAMEWORK_TEMPLATES } from '../../constants/retroFrameworks';
import { useDarkMode } from '../../context/DarkModeContext';

interface RetroFormProps {
  onSave?: (data: RetroFormData & { sections: Array<{ title: string; description: string; content: string }> }) => void;
}

interface RetroFormData {
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  frameworkType: keyof typeof FRAMEWORK_TEMPLATES;
}

interface FrameworkSection {
  id: string;
  label: string;
  description: string;
  placeholder: string;
}

const frameworkOptions = [
  {
    id: 'career-progress',
    title: 'Career Progress',
    description: 'Focus on professional growth, achievements, and career development goals.',
    icon: '📈'
  },
  {
    id: 'personal-growth',
    title: 'Personal Growth',
    description: 'Reflect on personal development, learning, and self-improvement.',
    icon: '🌱'
  },
  {
    id: 'start-stop-continue',
    title: 'Start Stop Continue',
    description: 'Analyze what to begin, cease, and maintain for better outcomes.',
    icon: '🔄'
  },
  {
    id: 'mad-sad-glad',
    title: 'Mad Sad Glad',
    description: 'Explore emotional responses to experiences and their impact.',
    icon: '😊'
  },
  {
    id: 'custom',
    title: 'Custom Framework',
    description: 'Create your own custom framework for specific needs.',
    icon: '✨'
  }
] as const;

const QUICK_DATE_RANGES = [
  {
    label: 'Last Week',
    getDates: () => ({
      start: subWeeks(new Date(), 1),
      end: new Date()
    })
  },
  {
    label: 'Last Month',
    getDates: () => ({
      start: subMonths(new Date(), 1),
      end: new Date()
    })
  },
  {
    label: 'This Quarter',
    getDates: () => ({
      start: startOfQuarter(new Date()),
      end: endOfQuarter(new Date())
    })
  },
  {
    label: 'Last Quarter',
    getDates: () => {
      const lastQuarter = subQuarters(new Date(), 1);
      return {
        start: startOfQuarter(lastQuarter),
        end: endOfQuarter(lastQuarter)
      };
    }
  }
] as const;

const RetroForm = forwardRef<HTMLFormElement, RetroFormProps>(({ onSave }, ref) => {
  const { darkMode } = useDarkMode();
  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<RetroFormData>({
    defaultValues: {
      title: '',
      description: '',
      startDate: new Date(),
      endDate: new Date(),
      frameworkType: 'career-progress'
    }
  });

  const frameworkType = watch('frameworkType');
  const selectedFramework = FRAMEWORK_TEMPLATES[frameworkType];
  const startDate = watch('startDate');

  const formatDateForInput = (date: Date): string => {
    try {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return new Date().toISOString().split('T')[0];
      }
      return format(date, 'yyyy-MM-dd');
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  const handleQuickDateSelect = (range: { start: Date; end: Date }) => {
    setValue('startDate', range.start);
    setValue('endDate', range.end);
  };

  const handleFormSubmit = (data: RetroFormData) => {
    if (onSave && selectedFramework) {
      onSave({
        ...data,
        sections: selectedFramework.sections.map((section: FrameworkSection) => ({
          title: section.label,
          description: section.description,
          content: ''
        }))
      });
    }
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Title */}
      <div>
        <label htmlFor="title" className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Title *
        </label>
        <Controller
          name="title"
          control={control}
          rules={{ required: 'Title is required' }}
          render={({ field: { onChange, value } }) => (
            <input
              type="text"
              id="title"
              value={value as string}
              onChange={onChange}
              autoFocus
              className={`mt-1 block w-full rounded-md shadow-sm ${
                darkMode
                  ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500'
                  : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500'
              } border focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}
              placeholder="e.g., Q1 2024 Career Retrospective"
            />
          )}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Description (optional)
        </label>
        <Controller
          name="description"
          control={control}
          render={({ field: { onChange, value } }) => (
            <textarea
              id="description"
              value={value as string}
              onChange={onChange}
              rows={3}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                darkMode
                  ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500'
                  : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500'
              } border focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}
              placeholder="What would you like to reflect on?"
            />
          )}
        />
      </div>

      {/* Framework Selection */}
      <div>
        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-3`}>
          Choose a Framework *
        </label>
        <Controller
          name="frameworkType"
          control={control}
          rules={{ required: 'Please select a framework' }}
          render={({ field: { onChange, value } }) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {frameworkOptions.map((framework) => (
                <button
                  key={framework.id}
                  type="button"
                  onClick={() => {
                    const frameworkId = framework.id as keyof typeof FRAMEWORK_TEMPLATES;
                    if (FRAMEWORK_TEMPLATES[frameworkId]) {
                      onChange(frameworkId);
                    }
                  }}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    value === framework.id
                      ? `border-blue-500 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`
                      : `border-gray-200 hover:border-blue-200 ${
                          darkMode ? 'border-gray-700 hover:border-blue-800' : ''
                        }`
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl" role="img" aria-hidden="true">
                      {framework.icon}
                    </span>
                    <div>
                      <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {framework.title}
                      </h3>
                      <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {framework.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        />
        {errors.frameworkType && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.frameworkType.message}</p>
        )}
      </div>

      {/* Date Range */}
      <div>
        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
          Select Time Period *
        </label>
        
        {/* Quick Date Range Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {QUICK_DATE_RANGES.map((range) => (
            <button
              key={range.label}
              type="button"
              onClick={() => handleQuickDateSelect(range.getDates())}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Date Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Start Date *
            </label>
            <Controller
              name="startDate"
              control={control}
              rules={{ required: 'Start date is required' }}
              render={({ field: { onChange, value } }) => (
                <input
                  type="date"
                  id="startDate"
                  value={formatDateForInput(value as Date)}
                  max={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const date = new Date(e.target.value);
                    onChange(date);
                  }}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white scheme-dark' 
                      : 'bg-white border-gray-300 text-gray-900 scheme-light'
                  } border focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}
                />
              )}
            />
            {errors.startDate && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.startDate.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="endDate" className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              End Date *
            </label>
            <Controller
              name="endDate"
              control={control}
              rules={{ required: 'End date is required' }}
              render={({ field: { onChange, value } }) => (
                <input
                  type="date"
                  id="endDate"
                  value={formatDateForInput(value as Date)}
                  min={formatDateForInput(startDate)}
                  max={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const date = new Date(e.target.value);
                    onChange(date);
                  }}
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white scheme-dark' 
                      : 'bg-white border-gray-300 text-gray-900 scheme-light'
                  } border focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm`}
                />
              )}
            />
            {errors.endDate && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.endDate.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Framework Preview */}
      {selectedFramework && (
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h3 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Framework Preview:
          </h3>
          <div className="space-y-2">
            {selectedFramework.sections.map((section) => (
              <div
                key={section.id}
                className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
              >
                • {section.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </form>
  );
});

RetroForm.displayName = 'RetroForm';

export default RetroForm; 